import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  WaterDrop as WaterIcon,
  Opacity as OpacityIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import waterReadingService from '../services/waterReadingService';
import api from '../services/api';

const WaterReadings = () => {
  const [readings, setReadings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReadings, setTotalReadings] = useState(0);
  const [filters, setFilters] = useState({
    room_id: '',
    status: '',
    billing_period: '',
  });
  const [statistics, setStatistics] = useState({
    total_consumption: 0,
    total_amount: 0,
    readings_count: 0,
    pending_billing: 0,
  });
  const [waterRate, setWaterRate] = useState(150);
  const [formData, setFormData] = useState({
    room_id: '',
    current_reading: '',
    reading_date: new Date().toISOString().split('T')[0],
    notes: '',
    previous_reading: 0,
    consumption: 0,
    amount: 0,
  });

  useEffect(() => {
    fetchRooms();
    fetchReadings();
    fetchStatistics();
    fetchWaterRate();
  }, [page, rowsPerPage, filters]);

  const fetchWaterRate = async () => {
    try {
      const response = await api.get('/api/config/system');
      if (response.data && response.data.water_rate_per_m3) {
        setWaterRate(parseFloat(response.data.water_rate_per_m3));
      }
    } catch (error) {
      console.error('Failed to fetch water rate:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await api.get('/api/rooms');
      // Filter rooms with active tenants
      const occupiedRooms = response.data.filter(r => r.status === 'occupied' && r.current_tenant_id);
      setRooms(occupiedRooms);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const fetchReadings = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.status) params.status = filters.status;
      if (filters.billing_period) params.billing_period = filters.billing_period;

      const response = await waterReadingService.getAllReadings(params);
      setReadings(response.data.items || []);
      setTotalReadings(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load readings:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load water readings');
      }
      setReadings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await waterReadingService.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      room_id: '',
      current_reading: '',
      reading_date: new Date().toISOString().split('T')[0],
      notes: '',
      previous_reading: 0,
      consumption: 0,
      amount: 0,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleRoomChange = async (roomId) => {
    setFormData({ ...formData, room_id: roomId });
    
    // Fetch latest reading for this room
    try {
      const response = await waterReadingService.getLatestReading(roomId);
      const previousReading = response.data.current_reading || response.data.previous_reading || 0;
      setFormData(prev => ({
        ...prev,
        room_id: roomId,
        previous_reading: previousReading,
      }));
    } catch (error) {
      console.error('Failed to fetch latest reading:', error);
      setFormData(prev => ({ ...prev, room_id: roomId, previous_reading: 0 }));
    }
  };

  const handleCurrentReadingChange = (value) => {
    const currentReading = parseFloat(value) || 0;
    const consumption = currentReading - formData.previous_reading;
    const amount = consumption * waterRate;
    
    setFormData({
      ...formData,
      current_reading: value,
      consumption: consumption >= 0 ? consumption : 0,
      amount: amount >= 0 ? amount : 0,
    });
  };

  const handleSubmit = async () => {
    if (!formData.room_id) {
      toast.error('Please select a room');
      return;
    }
    if (!formData.current_reading || formData.current_reading <= 0) {
      toast.error('Please enter a valid current reading');
      return;
    }
    if (parseFloat(formData.current_reading) < formData.previous_reading) {
      toast.error('Current reading cannot be less than previous reading');
      return;
    }

    try {
      const response = await waterReadingService.createReading({
        room_id: parseInt(formData.room_id),
        current_reading: parseFloat(formData.current_reading),
        reading_date: formData.reading_date,
        notes: formData.notes,
      });
      toast.success(
        `Water reading recorded! Invoice ${response.data.invoice_number || ''} generated automatically`,
        { autoClose: 5000 }
      );
      handleCloseDialog();
      fetchReadings();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to create reading:', error);
      toast.error(error.response?.data?.detail || 'Failed to record reading');
    }
  };


  const handleDeleteReading = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this reading? This will also delete the associated bill and invoice.')) {
      return;
    }

    try {
      await waterReadingService.deleteReading(readingId);
      toast.success('Reading, bill, and invoice deleted successfully');
      fetchReadings();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to delete reading:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete reading');
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'billed':
        return 'info';
      case 'paid':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading && readings.length === 0) {
    return <Loading message="Loading water readings..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Water Meter Readings"
        subtitle="Track water consumption and generate bills"
        breadcrumbs={[{ label: 'Water Readings' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchReadings();
                fetchStatistics();
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Record Reading
            </Button>
          </Box>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Consumption
              </Typography>
              <Typography variant="h4">{statistics.total_consumption.toFixed(2)} m³</Typography>
              <Typography variant="caption" color="textSecondary">
                Current Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">KES {statistics.total_amount.toLocaleString()}</Typography>
              <Typography variant="caption" color="textSecondary">
                Current Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Readings Recorded
              </Typography>
              <Typography variant="h4">{statistics.readings_count}</Typography>
              <Typography variant="caption" color="textSecondary">
                Current Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography color="inherit" variant="body2" gutterBottom>
                Pending Billing
              </Typography>
              <Typography variant="h4" color="inherit">{statistics.pending_billing}</Typography>
              <Typography variant="caption" color="inherit">
                Need to Generate Bills
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Filter by Room"
              name="room_id"
              value={filters.room_id}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Rooms</MenuItem>
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.room_number}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Filter by Status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="billed">Billed (Pending Payment)</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Filter by Period (YYYY-MM)"
              name="billing_period"
              value={filters.billing_period}
              onChange={handleFilterChange}
              size="small"
              placeholder="2026-01"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Readings Table */}
      {totalReadings === 0 ? (
        <EmptyState
          icon={WaterIcon}
          title="No Water Readings Yet"
          description="Start recording water meter readings to track consumption and generate bills."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Record First Reading
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Previous (m³)</strong></TableCell>
                <TableCell><strong>Current (m³)</strong></TableCell>
                <TableCell><strong>Consumption (m³)</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Invoice</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readings.map((reading) => (
                <TableRow key={reading.id}>
                  <TableCell>{reading.room_number}</TableCell>
                  <TableCell>{reading.tenant_name}</TableCell>
                  <TableCell>{parseFloat(reading.previous_reading).toFixed(2)}</TableCell>
                  <TableCell>{parseFloat(reading.current_reading).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {parseFloat(reading.consumption).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {parseFloat(reading.amount).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(reading.reading_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {reading.invoice_id ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <InvoiceIcon fontSize="small" color="primary" />
                        <Typography variant="body2">#{reading.invoice_id}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={reading.status.toUpperCase()}
                      color={getStatusColor(reading.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {reading.status === 'billed' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteReading(reading.id)}
                        title="Delete Reading, Bill & Invoice"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalReadings}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Record Reading Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <OpacityIcon sx={{ mr: 1 }} />
            Record Water Meter Reading
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Select Room"
            value={formData.room_id}
            onChange={(e) => handleRoomChange(e.target.value)}
            margin="normal"
            required
          >
            <MenuItem value="">Select a room</MenuItem>
            {rooms.map((room) => (
              <MenuItem key={room.id} value={room.id}>
                {room.room_number} - {room.property_name}
              </MenuItem>
            ))}
          </TextField>

          <Paper sx={{ p: 2, my: 2, bgcolor: 'info.lighter' }}>
            <Typography variant="body2" color="textSecondary">
              Previous Reading: <strong>{formData.previous_reading.toFixed(2)} m³</strong>
            </Typography>
          </Paper>

          <TextField
            fullWidth
            label="Current Reading (m³)"
            type="number"
            value={formData.current_reading}
            onChange={(e) => handleCurrentReadingChange(e.target.value)}
            margin="normal"
            required
            inputProps={{ step: '0.01', min: formData.previous_reading }}
          />

          {formData.current_reading && (
            <Paper sx={{ p: 2, my: 2, bgcolor: 'success.lighter' }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Consumption:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formData.consumption.toFixed(2)} m³
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount (@ KES {waterRate}/m³):
                  </Typography>
                  <Typography variant="h6" color="primary">
                    KES {formData.amount.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                ℹ️ Bill and invoice will be generated automatically
              </Typography>
            </Paper>
          )}

          <TextField
            fullWidth
            label="Reading Date"
            type="date"
            value={formData.reading_date}
            onChange={(e) => setFormData({ ...formData, reading_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Record Reading
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WaterReadings;
